import { z } from 'zod';
/**
 * Thought-record formats.
 *
 * Different problems want different worksheets. Forcing a rejection sting and a
 * task-paralysis spiral through the same seven columns is a design failure, and
 * the CBT literature has separate worksheets for exactly this reason.
 *
 * The important structural decision: every variant writes to the SAME columns
 * — situation, automatic thought, belief rating, emotions, two lists of
 * evidence, a response. What changes is the wording, which steps appear, and
 * the order. So trends, belief-shift maths and the log work identically across
 * all of them, and adding a format costs no schema change.
 *
 * `format` is stored on the record so the detail view can label the columns the
 * way the flow that produced them did. Reading back "Evidence it's true" on a
 * decatastrophising record would be nonsense.
 *
 * There are deliberately four variants, not ten. A menu of worksheets is a good
 * way to spend the moment deciding instead of writing.
 */
export const recordFormatKey = z.enum([
    'standard',
    'rejection',
    'stuck',
    'conflict',
    'catastrophe',
]);
const EMOTION_STEP = {
    field: 'emotions',
    title: 'What did you feel?',
    label: 'Pick what fits, then say how strong it was',
};
export const RECORD_FORMATS = [
    // -------------------------------------------------------------------------
    {
        key: 'standard',
        name: 'The usual one',
        when: 'A thought that keeps going round and you want to properly look at it.',
        beliefLabel: 'How much do you believe it, right now?',
        labels: {
            situation: 'What happened',
            thought: 'The thought',
            evidenceFor: "Evidence it's true",
            evidenceAgainst: 'Evidence it might not be',
            response: 'What you landed on',
        },
        steps: [
            {
                field: 'situation',
                title: 'What happened?',
                label: 'Just the facts',
                hint: 'What a camera would have caught. Leave out what it meant for now — that comes next.',
                placeholder: "I asked if he wanted help and he said he'd already asked me to look into it.",
            },
            EMOTION_STEP,
            {
                field: 'thought',
                title: 'What went through your mind?',
                label: 'The exact sentence in your head',
                hint: 'Not a summary — the actual words, however unreasonable they sound written down.',
                placeholder: 'He treats me like the help, not a partner.',
            },
            {
                field: 'evidenceFor',
                title: "What's the evidence it's true?",
                label: "Evidence it's true",
                hint: 'One per line. Facts, not feelings about the facts.',
                placeholder: 'He used a sharp tone\nHe cut me off',
            },
            {
                field: 'evidenceAgainst',
                title: "What's the evidence against it?",
                label: 'Evidence it might not be',
                hint: "This one is harder, and it's the one that does the work. Even small things count.",
                placeholder: 'He was mid-task and stressed\nHe asked me to hang out later',
            },
            {
                field: 'response',
                title: "So what's a fairer way to put it?",
                label: 'A fairer way to put it',
                hint: "Not a positive spin — something you'd actually believe. It usually sounds duller than the original, and that's the point.",
                placeholder: "He was stressed and mid-task. The tone was about the moment, not about what I'm worth to him.",
            },
        ],
    },
    // -------------------------------------------------------------------------
    // No "evidence it's true" step on purpose. With rejection sensitivity the
    // problem is not that the evidence went unweighed — it is the jump from a
    // small signal to a large meaning. Asking for evidence that you were
    // rejected mostly helps you build the case.
    {
        key: 'rejection',
        name: 'That landed hard',
        when: 'A curt reply, a changed plan, a look. Something small that hit far bigger than it should have.',
        beliefLabel: 'How true does that feel right now?',
        labels: {
            situation: 'What actually happened',
            thought: 'What you took it to mean',
            evidenceFor: 'What made it feel true',
            evidenceAgainst: 'Other explanations',
            response: 'A fairer read',
        },
        steps: [
            {
                field: 'situation',
                title: 'What actually happened?',
                label: 'Only what was literally said or done',
                hint: 'No tone, no interpretation, no what-it-meant. Just the words and the actions.',
                placeholder: 'He replied "k" and then went offline.',
            },
            {
                field: 'thought',
                title: 'What did you take it to mean?',
                label: 'What you concluded — about you',
                hint: "Say it plainly, even though it sounds like a lot to hang on one message. Especially then.",
                placeholder: "He's annoyed with me and is pulling away.",
            },
            EMOTION_STEP,
            {
                field: 'evidenceAgainst',
                title: 'What else could explain it?',
                label: 'Three explanations that have nothing to do with you',
                hint: 'Boring ones count. They are usually the right ones.',
                placeholder: 'He was driving\nHe was mid-conversation with someone else\nHis phone died',
            },
            {
                field: 'response',
                title: "So what's a fairer read?",
                label: 'A fairer read',
                hint: 'And separately: how much of the size of this is the event, and how much is the sting? Both can be real.',
                placeholder: "A one-word reply is a one-word reply. The size of my reaction is about rejection sensitivity, not about what he meant.",
            },
        ],
    },
    // -------------------------------------------------------------------------
    // Ends on a physical action rather than a reframe. When you cannot start, the
    // thought is often not even distorted — "this is too big" is frequently true.
    // What is missing is a first move small enough to make.
    {
        key: 'stuck',
        name: "Can't make myself start",
        when: "Staring at something you've been avoiding. Not lazy — stuck.",
        beliefLabel: 'How true does that feel?',
        labels: {
            situation: 'The task',
            thought: 'The story about it',
            evidenceFor: 'What makes it feel true',
            evidenceAgainst: "What's actually true",
            response: 'The first physical action',
        },
        steps: [
            {
                field: 'situation',
                title: 'What are you trying to start?',
                label: 'The actual task',
                hint: 'Be specific. "Sort my life out" is not a task.',
                placeholder: 'Reply to the email from the accountant.',
            },
            {
                field: 'thought',
                title: "What's the story about it?",
                label: "The sentence that's in the way",
                hint: 'The thing your brain says every time you look at it.',
                placeholder: "I've left it so long that replying now makes it worse.",
            },
            EMOTION_STEP,
            {
                field: 'evidenceAgainst',
                title: "What's actually true about it?",
                label: 'The boring facts',
                hint: 'How long did something like this really take last time? What actually happens if it slips another day?',
                placeholder: 'The last one took 11 minutes\nShe replied normally when I was late before',
            },
            {
                field: 'response',
                title: "What's the smallest first move?",
                label: 'The first physical action',
                hint: 'Not the plan. The first thing your hands do. "Open the folder" counts. If it feels too big, halve it.',
                placeholder: 'Open the email and read it once. Nothing else.',
            },
        ],
    },
    // -------------------------------------------------------------------------
    {
        key: 'conflict',
        name: 'After an argument',
        when: 'Once the heat has dropped but before you talk again.',
        beliefLabel: 'How much do you believe that?',
        labels: {
            situation: 'What happened',
            thought: 'What you concluded',
            evidenceFor: 'What made it feel true',
            evidenceAgainst: 'What might have been going on for them',
            response: 'Your part, and what you need',
        },
        steps: [
            {
                field: 'situation',
                title: 'What happened?',
                label: 'What was said and done',
                hint: 'Both sides. Facts only — tone and intent go in the next step.',
                placeholder: 'I said the plan had changed again and she said I was being dramatic.',
            },
            EMOTION_STEP,
            {
                field: 'thought',
                title: 'What did you conclude?',
                label: 'About them, or about you',
                placeholder: "She doesn't take anything I say seriously.",
            },
            {
                field: 'evidenceAgainst',
                title: 'What might have been going on for them?',
                label: 'Other explanations',
                hint: "Not excuses, and this isn't about letting it go. Just what else was in the room.",
                placeholder: "She'd had a bad day at work\nShe thought I was blaming her",
            },
            {
                field: 'response',
                title: 'What now?',
                label: 'Your part in one sentence, then what you need',
                hint: 'Your part with no "but" after it. Then what you need, as a request rather than an accusation.',
                placeholder: "I raised it at the worst possible moment. What I need is to know when plans change, even late.",
            },
        ],
    },
    // -------------------------------------------------------------------------
    // Keeps both evidence steps, because weighing likelihood is the mechanism
    // here — but ends on coping rather than on a reframe. "It probably won't
    // happen" is much weaker than "and if it did, here is what I'd do".
    {
        key: 'catastrophe',
        name: 'Spiralling about what might happen',
        when: 'Running the worst case on a loop about something that has not happened.',
        beliefLabel: 'How likely does it feel right now?',
        labels: {
            situation: "What you're worried about",
            thought: 'The worst case',
            evidenceFor: 'What makes it feel likely',
            evidenceAgainst: "What's actually most likely",
            response: 'How you would cope',
        },
        steps: [
            {
                field: 'situation',
                title: "What are you worried about?",
                label: 'The situation',
                placeholder: 'The review meeting on Thursday.',
            },
            {
                field: 'thought',
                title: "What's the worst that could happen?",
                label: 'Say the actual feared thing',
                hint: "Not a softened version. Written down, it usually looks smaller than it does at 3am.",
                placeholder: "They say the work isn't good enough and start managing me out.",
            },
            EMOTION_STEP,
            {
                field: 'evidenceFor',
                title: 'What makes it feel likely?',
                label: 'What feeds the fear',
                hint: 'One per line.',
                placeholder: 'I missed two deadlines last month',
            },
            {
                field: 'evidenceAgainst',
                title: "What's actually most likely?",
                label: 'The most likely version',
                hint: "Not the best case — the boring, probable one. What has actually happened the other times?",
                placeholder: 'Every other review has been fine\nShe already said the last piece was good',
            },
            {
                field: 'response',
                title: 'And if it did happen?',
                label: 'How you would cope',
                hint: "You have handled hard things before. What would you actually do — practically, on the day?",
                placeholder: "I'd ask what specifically needs to change and put it in writing. I'd talk to Sam. I'd start looking, without panicking.",
            },
        ],
    },
];
export const getRecordFormat = (key) => RECORD_FORMATS.find((f) => f.key === key) ?? RECORD_FORMATS[0];
//# sourceMappingURL=record-formats.js.map